import React, { useState, useEffect, useCallback } from 'react';
import Login from './components/Login';
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import Transactions from './components/Transactions';
import Upload from './components/Upload';
import Forecast from './components/Forecast';
import Insights from './components/Insights';
import Chatbot from './components/Chatbot';
import { healthCheck } from './services/api';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: '📊' },
  { id: 'transactions', label: 'Transactions', icon: '💳' },
  { id: 'upload', label: 'Upload', icon: '📤' },
  { id: 'forecast', label: 'Forecast', icon: '📈' },
  { id: 'insights', label: 'AI Insights', icon: '🤖' },
];

export default function App() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [serverStatus, setServerStatus] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Check if already logged in
  useEffect(() => {
    const saved = localStorage.getItem('user');
    if (saved) setUser(JSON.parse(saved));
  }, []);

  // Health check
  useEffect(() => {
    healthCheck()
      .then((r) => setServerStatus(r.data))
      .catch(() => setServerStatus({ status: 'offline' }));
  }, []);

  const triggerRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const handleLogin = (userData, token) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', token);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  const renderTab = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard refreshKey={refreshKey} />;
      case 'transactions':
        return <Transactions refreshKey={refreshKey} />;
      case 'upload':
        return <Upload onUploadSuccess={triggerRefresh} />;
      case 'forecast':
        return <Forecast refreshKey={refreshKey} />;
      case 'insights':
        return <Insights refreshKey={refreshKey} />;
      default:
        return <Dashboard refreshKey={refreshKey} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Navbar
        user={user}
        onLogout={handleLogout}
        serverStatus={serverStatus}
      />

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <nav className="flex space-x-1 bg-white/70 backdrop-blur-sm rounded-xl p-1.5 shadow-sm border border-white/50">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex-1 justify-center
                ${activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/25'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {renderTab()}
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-xs text-slate-400">
        © 2026 FinanceAI — AI-Driven Personal Finance Analytics
      </footer>
      <Chatbot />
    </div>
  );
}
